type HttpResponseError = {
  response: {
    status: number;
    data?: {
      code?: number;
    };
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toErrorCode = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const isHttpResponseError = (error: unknown): error is HttpResponseError => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return false;
  }

  return typeof error.response.status === 'number';
};

export const getErrorCode = (error: unknown): number | undefined => {
  if (!isRecord(error)) return undefined;
  const info = isRecord(error.info) ? error.info : undefined;
  const infoCode = toErrorCode(info?.errorCode);
  if (infoCode !== undefined) return infoCode;
  const response = isRecord(error.response) ? error.response : undefined;
  const data = response && isRecord(response.data) ? response.data : undefined;
  return toErrorCode(data?.code);
};

export const hasHttpResponseStatus = (
  error: unknown,
  status: number,
): boolean => isHttpResponseError(error) && error.response.status === status;

export const hasHttpResponseCode = (error: unknown, code: number): boolean =>
  getErrorCode(error) === code;

export const getHttpErrorMessage = (error: unknown, fallback: string) => {
  if (!isRecord(error)) return fallback;
  const info = isRecord(error.info) ? error.info : undefined;
  if (typeof info?.errorMessage === 'string' && info.errorMessage) {
    return info.errorMessage;
  }
  const response = isRecord(error.response) ? error.response : undefined;
  const data = response && isRecord(response.data) ? response.data : undefined;
  if (data) {
    if (typeof data.message === 'string' && data.message) return data.message;
    if (Array.isArray(data.message)) {
      return data.message.filter((item) => typeof item === 'string').join(', ');
    }
    if (typeof data.msg === 'string' && data.msg) return data.msg;
    if (typeof data.errorMessage === 'string' && data.errorMessage) {
      return data.errorMessage;
    }
  }
  if (typeof error.message === 'string' && error.message) return error.message;
  return fallback;
};
